import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import simplify from 'simplify-js';
import { svg } from 'd3';
import getClientDimensions from '../../../../../../../../../../shared/lib/get-client-dimensions/index';
import componentBase from '../../../../../../../../../../src/lib/component-base';
import AltAndPhase from './children/FlightMiniTrackerAltAndPhase';
import Bearing from './children/FlightMiniTrackerBearing';
import Durations from './children/FlightMiniTrackerDurations';
import Speed from './children/FlightMiniTrackerSpeed';

const normalPlaneIcon = require('../../../../../../../../../../static/images/plane-orange.svg');
const normalPlaneIconLanding = require('../../../../../../../../../../static/images/plane-orange-landing.png');
const xwingPlaneIcon = require('../../../../../../../../../../static/images/xwing/Xwing-Topdown.svg');
const xwingPlaneIconLanding = require('../../../../../../../../../../static/images/xwing/Xwing-Side.svg');

let Raphael;
let resizeTimeout;
// const windowWidth = 0.70;
// const domWidth = 0.95;
const domWidth = 0.95;

const resizeThrottlerFunctionGenerator = actualResizeHandler => () => {
  if (!resizeTimeout) {
    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      actualResizeHandler();
    }, 66);
  }
};

const simplificationToleranceGenerator = (positions) => {
  let t = 10;
  if (positions.length <= 100) {
    t = 1;
  } else if (positions.length <= 300) {
    t = 3;
  } else if (positions.length <= 500) {
    t = 5;
  }
  return t;
};

@componentBase('FlightMiniTracker')
export default class FlightMiniTracker extends React.Component {
  static propTypes = {
    flight: PropTypes.object.isRequired,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      visible: this._shouldShowGraph(),
      positions: this._processPositions(),
    };

    this._adjustGraphOnResize = this._adjustGraphOnResize.bind(this);
    this.currentDayFormatted = moment().format('MMM DD');

    const miniTrackerDivHeight = 125;
    const graphDivHeight = 98;
    const graphBottomDivBorderHeight = 2;
    this.muiDefaultProps = {
      divHeights: {
        miniTracker: miniTrackerDivHeight,
        graph: graphDivHeight,
        altAndPhase: 25,
      },
      graphAndPhase: {
        parentDivStyle: {
          padding: 0,
          height: `${miniTrackerDivHeight}px`,
          background: 'rgba(0, 0, 0, .5)',
        },
        graph: {
          parentDivStyle: {
            padding: 0,
            height: `${graphDivHeight}px`,
            backgroundImage: 'linear-gradient(0deg, transparent 3%, rgba(255, 255, 255, .2) 4%, rgba(255, 255, 255, .2) 5%, transparent 2%, transparent 3%, rgba(255, 255, 255, .2) 4%, rgba(255, 255, 255, .2) 5%, transparent 15%, transparent),' +
            'linear-gradient(90deg, transparent 3%, rgba(255, 255, 255, .2) 4%, rgba(255, 255, 255, .2) 5%, transparent 2%, transparent 3%, rgba(255, 255, 255, .2) 4%, rgba(255, 255, 255, .2) 5%, transparent 15%, transparent)',
            backgroundSize: '5px 5px',
            borderBottom: `${graphBottomDivBorderHeight}px solid #FF9600`,
          },
        },
      },
    };
  }

  componentDidMount() {
    Raphael = require('raphael');
    if (this._shouldShowGraph()) {
      this.resizeListener = resizeThrottlerFunctionGenerator(this._adjustGraphOnResize);
      window.addEventListener('resize', this.resizeListener);
      this._adjustGraphOnResize();
    }
  }

  componentWillReceiveProps() {
    this._adjustGraphOnResize();
    this.setState({
      visible: this._shouldShowGraph(),
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeListener);
  }

  _flexTrack() {
    let track = null;
    if (this.props && this.props.flight && this.props.flight.positional && this.props.flight.positional.flexTrack) {
      track = this.props.flight.positional.flexTrack;
    } else {
      track = null;
    }
    return track;
  }

  _shouldShowGraph() {
    const track = this._flexTrack();
    return track && track.positions.length > 0;
  }

  _processPositions() {
    const results = {
      simplifiedCoordinates: [],
      flightMaxAltitude: 0,
    };

    if (this._shouldShowGraph()) {
      const coordinates = [];
      const depTime = moment(this.props.flight.schedule.graphXAxis.depUTC);
      const track = this._flexTrack();
      const positions = track && track.positions;
      let flightMaxAltitude = 0;

      let isLanded = false;
      let firstLandedPosition = 0;

      for (let pos = 0; pos < positions.length; pos++) {
        const position = positions[pos];

        // if the flight is landed only use the first recored landed position
        if (position.phase && position.phase === 'landed') {
          isLanded = true;
          firstLandedPosition++;
        } else if (isLanded) {
          firstLandedPosition--;
          break;
        }
      }

      for (let pos = firstLandedPosition; pos < positions.length; pos++) {
        const position = positions[pos];
        const positionTime = moment(position.date);
        const positionTimeSinceScheduledDeparture = positionTime.diff(depTime, 'm');

        if (positions[pos].altitudeFt < 60000) {
          flightMaxAltitude = positions[pos].altitudeFt > flightMaxAltitude ? positions[pos].altitudeFt : flightMaxAltitude;

          if (!isNaN(positions[pos].altitudeFt) && positions[pos].altitudeFt > 0 && positionTimeSinceScheduledDeparture > 0) {
            coordinates.push({
              x: positionTimeSinceScheduledDeparture,
              y: positions[pos].altitudeFt,
              position,
            });
          }
        }
      }

      coordinates.push({ x: 0, y: 0 });
      results.flightMaxAltitude = flightMaxAltitude;
      results.simplifiedCoordinates = simplify(coordinates, simplificationToleranceGenerator(positions))
        .filter(crd => crd && crd.x != null && crd.y != null && !Number.isNaN(crd.x) && !Number.isNaN(crd.y));
    }

    return results;
  }

  get currentDay() {
    if (!this.currentDayFormatted) {
      this.currentDayFormatted = moment().format('MMM DD');
    }

    return this.currentDayFormatted;
  }

  get planeIcon() {
    if (this.currentDay === 'May 04') {
      return xwingPlaneIcon;
    }

    return normalPlaneIcon;
  }

  get planeLandingIcon() {
    if (this.currentDay === 'May 04') {
      return xwingPlaneIconLanding;
    }

    return normalPlaneIconLanding;
  }

  _getPhasePlaneIcon() {
    let phase = null;
    if (this.props.flight && this.props.flight.flightNote) {
      phase = this.props.flight.flightNote.phase;
    }

    /**
    * possible phases from flex :
    *  taxiing, takeoff, landed, landing,
    *  climbing, descending, level, cruising,
    *  approach, ground, departure
    *
    * possible phases derived from API :
    *  Cruising, Descending, Climbing, Landed,
    *  Grounded
    */

    switch (phase && phase.toLowerCase()) {
      case 'taxiing':
      case 'landed':
      case 'ground':
      case 'grounded':
      case 'departure':
        return {
          width: 41, height: 12, icon: this.planeLandingIcon, landingIcon: true,
        };
      case 'landing':
      case 'approach':
      case 'takeoff':
      case 'climbing':
      case 'descending':
      case 'level':
      case 'cruising':
      case 'unknown':
      default:
        return {
          width: 30, height: 30, icon: this.planeIcon, landingIcon: false,
        };
    }
  }

  _adjustGraphOnResize() {
    if (this.refs.graph) {
      const graphViewWidth = this.refs.graph.clientWidth;
      const newWidth = graphViewWidth * domWidth;
      // let newWidth = graphViewWidth;

      this.setState({ width: newWidth, positions: this._processPositions() });
      this._clearGraph();
      this._drawGraph();
    }
  }

  _clearGraph() {
    if (this.paper) {
      this.paper.clear();
    }
  }

  _drawGraph() {
    if (!this.props.flight.schedule || !this._flexTrack()) {
      this.logger('[FlightMiniTracker] _drawGraph : did not receive schedule/tracking info', this.props.flight.schedule);
      return;
    }

    let depTime;
    let arrTime;

    if (this.props.flight.schedule.graphXAxis) {
      depTime = moment(this.props.flight.schedule.graphXAxis.depUTC);
      arrTime = moment(this.props.flight.schedule.graphXAxis.arrUTC);
    } else {
      this.logger('[FlightMiniTracker] _drawGraph : no valid dep/arr times for flight', this.props.flight.schedule);
      this._clearGraph();
      return;
    }

    let flightTimeMinutes = arrTime.diff(depTime, 'm');
    const flightMaxAltitude = this.state.positions.flightMaxAltitude;
    const simplifiedCoordinates = this.state.positions.simplifiedCoordinates;

    // x variables
    const setWidth = (width) => {
      this.setState({ width });
      return width;
    };
    const [xWidth, xHeight] = getClientDimensions(document, 'trackerGraph');
    const xLength = this.state.width ? this.state.width : setWidth(xWidth * domWidth);
    // const xOffset = 5;
    const xOffset = 0;

    // y variables
    // const yLength = 100;
    const yLength = 83;
    // const yOffsetTop = 10;
    const yOffsetTop = 17;
    //  const yOffsetBottom = 0;

    const projectX = (unprojectedX) => {
      // update flightTimeMinutes from positional data
      if (unprojectedX > flightTimeMinutes) {
        flightTimeMinutes = unprojectedX;
      }
      return (unprojectedX / flightTimeMinutes) * xLength + xOffset;
    };

    const projectY = unprojectedY => (
      (yLength - (unprojectedY / flightMaxAltitude) * yLength) + yOffsetTop
    );

    const lineGenerator = svg.line() // eslint-disable-line
      .x(datum => projectX(datum.x))
      .y(datum => projectY(datum.y))
      .interpolate('basis');

    // flight Paths
    if (simplifiedCoordinates.length > 1) {
      this.paper = this.paper || Raphael(this.refs.graph, '100%', xHeight); // eslint-disable-line

      const originalFlightTimeMin = flightTimeMinutes;
      this.flightPath = this.paper.path(lineGenerator(simplifiedCoordinates))
        .attr('stroke', '#FF9600')
        .attr('stroke-width', '2');
      const lastSeen = simplifiedCoordinates[0];

      // rescale x based on flight time minutes coming from positions array
      if (originalFlightTimeMin !== flightTimeMinutes || (this.props.flight.isLanded && lastSeen.x < flightTimeMinutes)) {
        flightTimeMinutes = lastSeen.x;
        this.flightPath.remove();
        this.flightPath = this.paper.path(lineGenerator(simplifiedCoordinates))
          .attr('stroke', '#FF9600')
          .attr('stroke-width', '2');
      }

      const planePhaseIcon = this._getPhasePlaneIcon();

      if (projectX(lastSeen.x) > 0) {
        // where the current point is (plus half the plane icon width for non-landing flights)
        let vertLineLoc = projectX(lastSeen.x) + planePhaseIcon.width / 2 + 3;
        if (planePhaseIcon.landingIcon) {
          vertLineLoc = projectX(lastSeen.x) + planePhaseIcon.width / 2;
        }
        const vertLineLocBackFill = vertLineLoc - 1;

        // orange 'where the flight has been' fill-in
        this.paper.path(`M${vertLineLocBackFill / 2},0 L${vertLineLocBackFill / 2},100`)
          .attr('stroke', 'rgba(250, 168, 23, 0.2)')
          .attr('stroke-width', vertLineLocBackFill);

        // current location vertical line
        this.paper.path(`M${vertLineLoc},0 L${vertLineLoc},100`)
          .attr('stroke', 'rgba(255, 255, 255, 0.6)')
          .attr('stroke-width', '2');

        if (!planePhaseIcon.landingIcon) {
          this.paper.image(
            planePhaseIcon.icon, projectX(lastSeen.x) - 2,
            projectY(lastSeen.y) - planePhaseIcon.height / 2, planePhaseIcon.width, planePhaseIcon.height,
          );
        } else {
          this.paper.image(
            planePhaseIcon.icon, projectX(lastSeen.x) - planePhaseIcon.width / 2,
            projectY(lastSeen.y) - planePhaseIcon.height, planePhaseIcon.width, planePhaseIcon.height,
          );
        }
      }
    } else {
      this.logger(
        '[FlightMiniTracker] _drawGraph : cannot draw flight path from given positional data',
        simplifiedCoordinates,
      );
      this._clearGraph();
    }
  }

  _flightInFinalState() {
    if (this.props.flight && this.props.flight.status && this.props.flight.status.statusCode) {
      return (this.props.flight.status.statusCode === 'C' || //  cancelled
              this.props.flight.status.statusCode === 'D' || //  diverted
              this.props.flight.status.statusCode === 'U' || //  unknown
              this.props.flight.status.statusCode === 'DN' || //  data needed (unknown)
              this.props.flight.status.statusCode === 'NO' || //  not operational (unknown)
              this.props.flight.status.statusCode === 'L'); //  arrived
    }

    return true; // no flight or status; assume final state
  }

  _getLastSeenPositionAttribute(attributeName) {
    let value = null;

    if (this.state.positions &&
      this.state.positions.simplifiedCoordinates &&
      this.state.positions.simplifiedCoordinates.length > 0 &&
      this.state.positions.simplifiedCoordinates[0].position) {
      value = this.state.positions.simplifiedCoordinates[0].position[attributeName];
    }

    return value;
  }

  _getAltAndPhaseProps() {
    const props = {
      defRowHeight: this.muiDefaultProps.divHeights.altAndPhase,
      currentAltitude: null,
      currentPhase: null,
    };

    props.currentAltitude = this._getLastSeenPositionAttribute('altitudeFt');

    if (this.props.flight.flightNote) {
      props.currentPhase = this.props.flight.flightNote.phase;
    }

    return props;
  }

  _getBearingProps() {
    const props = { currentBearing: null, currentHeading: null, currentDay: this.currentDay };
    const track = this._flexTrack();
    if (!this._flightInFinalState() && track) {
      if (track.bearing) {
        props.currentBearing = track.bearing;
      }

      if (track.heading) {
        props.currentHeading = track.heading;
      }
    }

    return props;
  }

  _getSpeedProps() {
    const props = { currentSpeedMPH: null, asKnots: false };

    if (!this._flightInFinalState()) {
      props.currentSpeedMPH = this._getLastSeenPositionAttribute('speedMph');

      if (props.currentSpeedMPH) {
        props.asKnots = true;
      }
    }

    return props;
  }

  _getDurationsProps() {
    const props = {
      isLanded: ('isLanded' in this.props.flight) ? this.props.flight.isLanded : null,
      depDateTimeUTC: null,
      arrDateTimeUTC: null,
      currentPositionTime: null,
    };

    if (this.props.flight.schedule && this.props.flight.schedule.graphXAxis) {
      props.depDateTimeUTC = this.props.flight.schedule.graphXAxis.depUTC;
      props.arrDateTimeUTC = this.props.flight.schedule.graphXAxis.arrUTC;
    }

    props.currentPositionTime = this._getLastSeenPositionAttribute('date');

    return props;
  }

  render() {
    const blockOrNone = this._shouldShowGraph() ? 'block' : 'none';

    // this.logger('[FlightMiniTracker] render : show? ', this._shouldShowGraph());
    // this.logger('[FlightMiniTracker] render : display : ', blockOrNone);
    // this.logger('[FlightMiniTracker] render : props', this.props);

    const graph = (
      <div id='trackerGraph' className='row' style={this.muiDefaultProps.graphAndPhase.graph.parentDivStyle}>
        <div className='col-sm-12 col-md-12' style={{ padding: 0, height: 'inherit', display: blockOrNone }} ref='graph' />
      </div>);

    return (
      <div className='row' style={{ padding: '10px' }}>
        <div className='row' style={{ paddingBottom: '5px' }}>
          <span style={{ fontSize: '18pt', fontWeight: 700 }}>Tracking Details</span>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-12' style={{ padding: '0', border: '0px solid white' }}>
            <div className='col-xs-12 col-sm-12 col-md-6 col-lg-6' style={{ padding: 0 }}>
              <div className='col-md-12' style={{ padding: 0, border: '1px solid white' }}>
                <div className='col-md-12' style={this.muiDefaultProps.graphAndPhase.parentDivStyle}>
                  { graph }
                  <AltAndPhase {...this._getAltAndPhaseProps()} />
                </div>
              </div>
            </div>
            <div className='col-xs-12 col-sm-12 col-md-6 col-lg-6' style={{ padding: 0 }}>
              <div className='col-xs-12 col-sm-12 col-md-12' style={{ padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px` }}>
                <div className='col-xs-4 col-sm-4 col-md-4' style={{ display: this._flightInFinalState() ? 'none' : 'block', padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px` }}>
                  <div className='col-md-12' style={{ padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px`, border: '1px solid white' }}>
                    <Bearing {...this._getBearingProps()} />
                  </div>
                </div>
                <div className='col-xs-4 col-sm-4 col-md-4' style={{ display: this._flightInFinalState() ? 'none' : 'block', padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px` }}>
                  <div className='col-md-12' style={{ padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px`, border: '1px solid white' }}>
                    <Speed {...this._getSpeedProps()} />
                  </div>
                </div>
                <div className='col-xs-4 col-sm-4 col-md-4' style={{ padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px` }}>
                  <div className='col-md-12' style={{ padding: 0, height: `${this.muiDefaultProps.divHeights.miniTracker}px`, border: '1px solid white' }}>
                    <Durations {...this._getDurationsProps()} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-sm-0 col-md-0 col-lg-0' style={{ padding: 0 }} />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/SingleFlight/children/FlightTracker/children/FlightPositionalInformationSection/children/FlightPositionalInformation/children/FlightMiniTracker/FlightMiniTracker.js